import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources = [], videoElement } = this.refs;
    
    if (!videoElement) {
      return;
    }

    // Set sources
    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
      }
    }

    // Load and play video with Safari-specific handling
    videoElement.load();
    
    // Safari requires explicit play() after load, even with autoplay attribute
    const playVideo = () => {
      // Ensure video is muted for autoplay to work in all browsers
      videoElement.muted = true;
      
      // Attempt to play
      const playPromise = videoElement.play();
      
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Autoplay was prevented - retry on user interaction
            const playOnInteraction = () => {
              videoElement.play().catch(() => {});
              document.removeEventListener('click', playOnInteraction);
              document.removeEventListener('touchstart', playOnInteraction);
            };
            
            document.addEventListener('click', playOnInteraction, { once: true });
            document.addEventListener('touchstart', playOnInteraction, { once: true });
          });
      }
    };

    // Try to play immediately if video is ready
    if (videoElement.readyState >= 3) {
      playVideo();
    } else {
      // Wait for video to be ready
      videoElement.addEventListener('canplaythrough', playVideo, { once: true });
      
      // Fallback to loadeddata event
      videoElement.addEventListener('loadeddata', () => {
        if (videoElement.paused) {
          playVideo();
        }
      }, { once: true });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
